// Rol activo + identidad de quien ha entrado.
// El rol REAL llega del claim `roles` del ID token de Entra (App Roles del
// registro «Ona Pulse»). El selector manual sigue existiendo, pero cuando hay
// sesión de Entra queda declarado como SIMULACIÓN: cambia lo que se ve, no lo
// que se es (la identidad y el rol de verdad se siguen enseñando en la ficha).
import React, { createContext, useContext, useMemo, useState } from 'react';
import { ROLES, type Role } from '@/constants/data';
import { type Sesion } from '@/lib/auth';

interface RoleCtx {
  role: Role;                   // el que pinta la portada (real o simulado)
  setRoleId: (id: Role['id']) => void;
  sesion: Sesion | null;        // null = vista previa sin login (Expo Go)
  rolReal: Role | null;         // el PRINCIPAL, el que abre la app
  /** TODOS los roles que Hotelos le ha dado. Puede tener varios (migr 175) y
      pasar de uno a otro no es simular: son suyos. */
  mios: Role[];
  simulando: boolean;           // se está mirando un área que NO es suya
  salir: () => void;            // cerrar sesión (lo inyecta la puerta de login)
  /* Lo que el SERVIDOR dice que esta persona puede ver. null = no se sabe
     (vista previa o token heredado) → se enseña todo, como hasta ahora.
     🔴 Esto NO es la seguridad: el servidor ya recorta. Es para no pintar
     ceros donde en realidad no hay permiso, que se leería como «cero euros». */
  puede: (cap: string) => boolean;
}

const Ctx = createContext<RoleCtx>({
  role: ROLES[0], setRoleId: () => {}, sesion: null, rolReal: null, mios: [], simulando: false,
  salir: () => {}, puede: () => true,
});

// 🔴 Hotelos guarda el rol en MAYÚSCULAS ('SIS') y el catálogo de la app en
// minúsculas ('sis'): sin normalizar, no casaba y se caía al rol de Entra.
const buscaRol = (id?: string | null) =>
  ROLES.find((r) => r.id === String(id ?? '').trim().toLowerCase()) ?? null;

export function RoleProvider({ children, sesion = null, onSalir, capacidades = null, rolServidor = null, rolesServidor = [] }: {
  children: React.ReactNode;
  sesion?: Sesion | null;
  onSalir?: () => void;
  capacidades?: string[] | null;
  rolServidor?: string | null;
  /** TODOS sus roles. El principal abre la app; los demás también son suyos. */
  rolesServidor?: string[];
}) {
  // 🔴 MANDA HOTELOS, y punto. El rol del token de Entra SOLO se usa cuando el
  // servidor no ha podido contestar (sin red): si contestó y dijo que no hay
  // rol, no hay rol — inventárselo desde Entra sería decir que tienes uno que
  // nadie te ha dado. `capacidades === null` es justo «no se sabe».
  const servidorContesto = capacidades !== null;
  const rolReal = buscaRol(rolServidor) ?? (servidorContesto ? null : buscaRol(sesion?.rol));
  /* 🔴 SUS roles, todos. Hotelos permite varios por persona (migr 175) y la app
     solo enseñaba el principal: quien era Sistemas y Financiero veía Sistemas y
     se quedaba sin la otra portada. Cambiar entre ESTOS no es «simular» —son
     suyos—; simular es mirar la de un área que no te toca, y eso sigue pidiendo
     la capacidad SIMULAR_ROL. */
  const mios = (rolesServidor.length ? rolesServidor : (rolServidor ? [rolServidor] : []))
    .map(buscaRol).filter((r): r is Role => !!r);
  // El rol inicial es el de Entra; sin sesión (vista previa) arranca en Dirección.
  const [role, setRole] = useState<Role>(rolReal ?? ROLES[0]);
  // Si la sesión cambia (login, renovación con rol distinto), manda Entra.
  const [ultimoReal, setUltimoReal] = useState<string | null>(rolReal?.id ?? null);
  if ((rolReal?.id ?? null) !== ultimoReal) {
    setUltimoReal(rolReal?.id ?? null);
    setRole(rolReal ?? ROLES[0]);
  }

  const value = useMemo<RoleCtx>(() => ({
    role,
    setRoleId: (id) => setRole(ROLES.find((r) => r.id === id) ?? ROLES[0]),
    sesion,
    rolReal,
    /* Simular es mirar la portada de un área que NO es tuya. Pasar a otro rol
       propio no lo es, y la app no debe llamarlo así. */
    simulando: !!rolReal && role.id !== rolReal.id && !mios.some((r) => r.id === role.id),
    mios,
    salir: onSalir ?? (() => {}),
    puede: (cap: string) => !capacidades || capacidades.includes(cap),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [role, sesion, rolReal, onSalir, capacidades, rolesServidor.join()]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useRole = () => useContext(Ctx);
